import {
  IconRefresh,
  IconClock,
  IconArrowLeft,
  IconPlayerTrackNext,
  IconPlayerTrackPrev,
  IconPlayerPlay,
  IconPlayerPause,
  IconPlayerStop,
  IconCards,
  IconLayoutDashboard,
  IconTable,
  IconUsers,
  IconList,
  IconCheck,
  IconDownload,
  IconCash,
  IconLockOpen,
  IconPencil,
  IconReceipt,
  IconPrinter,
  IconTrophy,
  IconPlus,
  IconTarget,
  IconFile,
  IconCalendar,
  IconDeviceTv,
  IconSettings,
  IconShield,
  IconHistory,
  IconPalette,
  IconBuilding,
  IconTool,
  IconUser,
  IconHome,
  IconStar,
  IconDeviceDesktop,
  IconDeviceMobile,
  IconWorld,
  IconMail,
  IconLayout,
  IconListDetails,
  IconPercentage,
  IconUserCog,
  IconUsersGroup,
  IconAddressBook,
  IconClipboardList,
  IconUserCheck,
  IconFileImport,
  IconCrown,
  IconLayoutSidebar,
  IconWallet,
  IconTicket,
  IconId,
  IconCalendarUser,
  IconCoin,
  IconCashRegister,
  IconLock,
  IconReportMoney,
  IconStairsUp,
  IconClockDollar,
  IconTemplate,
  IconCalendarEvent,
  IconPackage,
  IconAdjustments,
  IconBuildingStore,
  IconLanguage,
  IconCode,
  IconChartBar,
} from '@tabler/icons-react';

/** Named icons offered by the page-settings picker, in display order. */
export const ICON_CHOICES = [
  'dashboard',
  'table',
  'users',
  'user',
  'user-cog',
  'users-group',
  'address-book',
  'list',
  'list-details',
  'clipboard-list',
  'check',
  'user-check',
  'download',
  'file-import',
  'star',
  'crown',
  'layout',
  'layout-sidebar',
  'wallet',
  'ticket',
  'id',
  'calendar-user',
  'cash',
  'coin',
  'cash-register',
  'lock-open',
  'lock',
  'pencil',
  'receipt',
  'report-money',
  'printer',
  'trophy',
  'plus',
  'target',
  'stairs-up',
  'clock-dollar',
  'percentage',
  'file',
  'template',
  'calendar',
  'calendar-event',
  'device-tv',
  'package',
  'settings',
  'adjustments',
  'shield',
  'building',
  'building-store',
  'home',
  'clock',
  'palette',
  'device-desktop',
  'device-mobile',
  'world',
  'language',
  'mail',
  'tool',
  'code',
  'history',
  'cards',
  'chart-bar',
] as const;

const named: Record<(typeof ICON_CHOICES)[number], typeof IconCheck> = {
  dashboard: IconLayoutDashboard,
  table: IconTable,
  users: IconUsers,
  user: IconUser,
  'user-cog': IconUserCog,
  'users-group': IconUsersGroup,
  'address-book': IconAddressBook,
  list: IconList,
  'list-details': IconListDetails,
  'clipboard-list': IconClipboardList,
  check: IconCheck,
  'user-check': IconUserCheck,
  download: IconDownload,
  'file-import': IconFileImport,
  star: IconStar,
  crown: IconCrown,
  layout: IconLayout,
  'layout-sidebar': IconLayoutSidebar,
  wallet: IconWallet,
  ticket: IconTicket,
  id: IconId,
  'calendar-user': IconCalendarUser,
  cash: IconCash,
  coin: IconCoin,
  'cash-register': IconCashRegister,
  'lock-open': IconLockOpen,
  lock: IconLock,
  pencil: IconPencil,
  receipt: IconReceipt,
  'report-money': IconReportMoney,
  printer: IconPrinter,
  trophy: IconTrophy,
  plus: IconPlus,
  target: IconTarget,
  'stairs-up': IconStairsUp,
  'clock-dollar': IconClockDollar,
  percentage: IconPercentage,
  file: IconFile,
  template: IconTemplate,
  calendar: IconCalendar,
  'calendar-event': IconCalendarEvent,
  'device-tv': IconDeviceTv,
  package: IconPackage,
  settings: IconSettings,
  adjustments: IconAdjustments,
  shield: IconShield,
  building: IconBuilding,
  'building-store': IconBuildingStore,
  home: IconHome,
  clock: IconClock,
  palette: IconPalette,
  'device-desktop': IconDeviceDesktop,
  'device-mobile': IconDeviceMobile,
  world: IconWorld,
  language: IconLanguage,
  mail: IconMail,
  tool: IconTool,
  code: IconCode,
  history: IconHistory,
  cards: IconCards,
  'chart-bar': IconChartBar,
};

const icons: Record<string, typeof IconCheck> = {
  ...named,
  print: IconPrinter,
  receipt_long: IconReceipt,
  build: IconTool,
  restart_alt: IconRefresh,
  timer: IconClock,
  tv: IconDeviceTv,
  arrow_back: IconArrowLeft,
  fast_forward: IconPlayerTrackNext,
  groups: IconUsers,
  pause: IconPlayerPause,
  payments: IconCash,
  play_arrow: IconPlayerPlay,
  schedule: IconClock,
  skip_next: IconPlayerTrackNext,
  skip_previous: IconPlayerTrackPrev,
  stop: IconPlayerStop,
  style: IconCards,

  '⊞': IconLayoutDashboard,
  '🎲': IconTable,
  '👥': IconUsers,
  '👤': IconUser,
  '🔐': IconShield,
  '🏠': IconHome,
  '⭐': IconStar,
  '⏱️': IconClock,
  '🖥️': IconDeviceDesktop,
  '📱': IconDeviceMobile,
  '🌐': IconWorld,
  '📧': IconMail,
  '▣': IconLayout,
  '▤': IconListDetails,
  '％': IconPercentage,
  '📋': IconList,
  '✅': IconCheck,
  '📥': IconDownload,
  '💰': IconCash,
  '🔓': IconLockOpen,
  '📝': IconPencil,
  '🧾': IconReceipt,
  '🖨️': IconPrinter,
  '🏆': IconTrophy,
  '➕': IconPlus,
  '🎯': IconTarget,
  '📄': IconFile,
  '📅': IconCalendar,
  '📺': IconDeviceTv,
  '⚙️': IconSettings,
  '🛡️': IconShield,
  '📜': IconHistory,
  '🎨': IconPalette,
  '🏢': IconBuilding,
  '🔧': IconTool,
};

/** Preserve custom text glyphs; map the built-in visual vocabulary to Tabler. */
export function BrandIcon({ value, size = 20 }: { value: string; size?: number }) {
  const Icon = icons[value];
  if (!Icon) return <span aria-hidden="true">{value}</span>;
  return <Icon size={size} stroke={1.5} aria-hidden="true" className="shrink-0 inline-block" />;
}
